import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import Multiselect, {
  MultiselectProps,
} from '@amzn/awsui-components-console/multiselect';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useDescribeCxFamilyQuery } from 'src/generated/graphql';
import { getCxIdsError } from 'src/features/namespace/components/createStringGroupValidation';
import { GroupEdits, StringGroup } from '../types';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface EditGroupModalProps {
  visible: boolean;
  group: StringGroup;
  draftEdits?: GroupEdits;
  /**
   * Persists the group metadata edit into the draft. The parent is responsible
   * for closing this modal and (when appropriate) chaining into the
   * submit-for-translation flow — see StringGroupDetailPage.handleEditGroupSave.
   */
  onSave: (edits: GroupEdits) => void;
  onDismiss: () => void;
}

const EditGroupModal: React.FC<EditGroupModalProps> = ({
  visible,
  group,
  draftEdits,
  onSave,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [selectedCxIds, setSelectedCxIds] = useState<MultiselectProps.Option[]>(
    [],
  );
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data: cxFamilyData,
    loading: cxIdsLoading,
    error: cxIdsError,
  } = useDescribeCxFamilyQuery({
    variables: { cxFamilyId: group.cxFamilyId },
    skip: !visible || !group.cxFamilyId,
  });

  const cxIdOptions = useMemo<MultiselectProps.Option[]>(
    () =>
      (cxFamilyData?.describeCxFamily.cxFamily.cxIds ?? []).map(({ cxId }) => ({
        label: cxId,
        value: cxId,
      })),
    [cxFamilyData],
  );

  const effectiveDescription =
    draftEdits?.description ?? group.description ?? '';
  const effectiveCxIds = draftEdits?.cxIds ?? group.cxIds;

  // Keep a ref to the latest effective CX IDs so the open effect can read them
  // without listing them as dependencies (which would re-fire mid-edit).
  const effectiveCxIdsRef = useRef(effectiveCxIds);
  effectiveCxIdsRef.current = effectiveCxIds;

  const prevVisibleRef = useRef(false);

  // Baseline snapshot captured at open time — the exact CX IDs the form was
  // initialised from. `cxIdsChanged()` compares against THIS frozen snapshot
  // rather than the live effective value, so a background refetch that lands
  // while the modal is open can't make the save-time baseline diverge from
  // what the user actually sees. Comparing against live props instead would
  // spuriously trip the confirmation dialog.
  const baselineCxIdsRef = useRef(effectiveCxIds);

  // Re-initialise form state only on the visible false→true transition, before
  // paint: a passive effect would commit one frame with nothing selected, which
  // is long enough to flash the empty-selection error.
  // A saved modal edit belongs to the local draft until the whole group is
  // submitted, so reopening must use draft values ahead of server values.
  // We intentionally ignore prop changes while the modal is already open —
  // overwriting the user's in-progress edits (e.g. because a background
  // refetch landed a new array reference) is worse than showing stale
  // initial values; the user is actively editing and their input takes
  // precedence.
  useLayoutEffect(() => {
    const wasVisible = prevVisibleRef.current;
    prevVisibleRef.current = visible;

    if (visible && !wasVisible) {
      const initialCxIds = effectiveCxIdsRef.current;
      // Freeze the baseline used by change-detection to the CX IDs the form is
      // being seeded with, so they stay in lockstep.
      baselineCxIdsRef.current = initialCxIds;
      setSelectedCxIds(initialCxIds.map((id) => ({ label: id, value: id })));
      setShowConfirmation(false);
    }
  }, [visible]);

  const cxIdsChanged = useCallback((): boolean => {
    const currentSet = new Set(baselineCxIdsRef.current);
    const selectedSet = new Set(
      selectedCxIds.map((opt) => opt.value).filter(Boolean) as string[],
    );
    if (currentSet.size !== selectedSet.size) return true;
    for (const id of currentSet) {
      if (!selectedSet.has(id)) return true;
    }
    return false;
  }, [selectedCxIds]);

  // A group always opens with at least one CX ID, so an empty box is the user's
  // own deselection — unlike create, there is no untouched state to suppress.
  const emptyCxIdsError = getCxIdsError(t, selectedCxIds.length, true);

  const handleSave = useCallback(() => {
    // CX IDs are the only editable field (description is shown read-only), so a
    // CX ID change is the only thing worth confirming; anything else is a no-op
    // that just closes the modal.
    if (cxIdsChanged()) {
      setShowConfirmation(true);
      return;
    }
    onDismiss();
  }, [cxIdsChanged, onDismiss]);

  const handleConfirm = useCallback(() => {
    // Persist the CX ID change into the draft. The parent then closes this modal
    // and opens the submit-for-translation flow (see handleEditGroupSave), so the
    // translation is submitted right after the group update. `description` is
    // read-only here but carried through so the draft keeps the group's current
    // value alongside the edited CX IDs.
    onSave({
      description: effectiveDescription,
      cxIds: selectedCxIds.map((opt) => opt.value).filter(Boolean) as string[],
    });
    setShowConfirmation(false);
  }, [onSave, selectedCxIds, effectiveDescription]);

  const handleConfirmCancel = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  return (
    <>
      <Modal
        visible={visible && !showConfirmation}
        onDismiss={onDismiss}
        header={t('sgd-edit-group-title')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>
                {t('string-cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!!emptyCxIdsError}
              >
                {t('sgd-save-changes')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label={t('sgd-field-name')}>
            <Box fontWeight="bold" fontSize="body-m">
              {group.stringGroupId}
            </Box>
          </FormField>

          <FormField label={t('string-meta-cx-family')}>
            <Box color="text-body-secondary">{group.cxFamilyId}</Box>
          </FormField>

          <FormField
            label={t('sgd-select-cx-ids')}
            constraintText={t('sgd-cx-ids-help')}
            errorText={emptyCxIdsError}
          >
            <Multiselect
              data-testid="edit-group-cxid-select"
              selectedOptions={selectedCxIds}
              onChange={({ detail }) =>
                setSelectedCxIds([...detail.selectedOptions])
              }
              enableSelectAll
              i18nStrings={{ selectAllText: t('create-sg-cx-ids-select-all') }}
              options={cxIdOptions}
              filteringType="auto"
              filteringPlaceholder={t('sgd-cx-id-filter-placeholder')}
              statusType={
                cxIdsError ? 'error' : cxIdsLoading ? 'loading' : 'finished'
              }
              loadingText={t('sgd-cx-ids-loading')}
              errorText={t('sgd-cx-ids-error')}
              empty={t('sgd-cx-ids-empty')}
              disabled={cxIdsLoading || !!cxIdsError}
              placeholder={t('sgd-select-cx-ids')}
            />
          </FormField>

          <FormField label={t('sgd-field-team-name')}>
            <Box color="text-body-secondary">{group.teamName ?? '—'}</Box>
          </FormField>

          <FormField label={t('sgd-field-source-locale')}>
            <Box color="text-body-secondary">{group.sourceLocale}</Box>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Confirmation sub-modal for CX ID changes */}
      <Modal
        visible={showConfirmation}
        onDismiss={handleConfirmCancel}
        header={t('sgd-confirm-changes')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleConfirmCancel}>
                {t('string-cancel')}
              </Button>
              <Button variant="primary" onClick={handleConfirm}>
                {t('sgd-confirm')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Alert type="warning">{t('sgd-edit-group-cxid-warning')}</Alert>
      </Modal>
    </>
  );
};

export default EditGroupModal;
